import { Color } from './java/Color';
import { Graphics2D } from './java/Graphics2D';
import { Rectangle } from './java/Geom';
import { GameObject } from './GameObject';
import { Handler } from './Handler';
import { ID } from './ID';
import { TrailPool } from './TrailPool';
import { Game } from './Game';

export class SmartEnemy extends GameObject {
  private static readonly SIZE = 32;
  private static readonly FILL = new Color(199, 125, 255);
  private static readonly GLOW = new Color(199, 125, 255, 35);
  private static readonly TRAIL_COLOR = new Color(199, 125, 255);

  private handler: Handler;
  private player!: GameObject;
  private trailTick = 0;
  private pulsePhase = 0;
  private readonly boundsRect = new Rectangle();
  private hp = -1;

  constructor(x: number, y: number, id: ID, handler: Handler) {
    super(x, y, id);
    this.handler = handler;
    const objects: GameObject[] = handler.getObjects();
    for (let i = 0; i < objects.length; i++) {
      if (objects[i].getId() === ID.Player) {
        this.player = objects[i];
      }
    }
  }

  setCombatHp(hp: number): void { this.hp = hp; }

  override takeDamage(dmg: number): boolean {
    if (this.hp < 0) return false;
    this.hp -= dmg;
    if (this.hp <= 0) { this.die(); return true; }
    return true;
  }

  private die(): void {
    const half = Math.trunc(SmartEnemy.SIZE / 2);
    for (let i = 0; i < 6; i++) {
      const angle = Math.random() * Math.PI * 2;
      TrailPool.add(
        this.x + half + Math.cos(angle) * 8,
        this.y + half + Math.sin(angle) * 8,
        SmartEnemy.FILL, half, half, 0.05, TrailPool.SHAPE_CIRCLE);
    }
    Game.addEnemyKilled();
    Game.addKillBonus(125);
    this.handler.removeObject(this);
  }

  getBounds(): Rectangle {
    this.boundsRect.x = Math.trunc(this.x);
    this.boundsRect.y = Math.trunc(this.y);
    this.boundsRect.width = SmartEnemy.SIZE;
    this.boundsRect.height = SmartEnemy.SIZE;
    return this.boundsRect;
  }

  tick(): void {
    this.x += this.velX;
    this.y += this.velY;
    const diffX = this.x - this.player.getX() - 16;
    const diffY = this.y - this.player.getY() - 16;
    const distance = Math.sqrt(
      (this.x - this.player.getX()) * (this.x - this.player.getX())
      + (this.y - this.player.getY()) * (this.y - this.player.getY()));
    this.velX = ((-2 / distance) * diffX);
    this.velY = ((-2 / distance) * diffY);

    // Pulse faster when closer to player
    const closeness = Math.max(0, 1 - distance / 300);
    this.pulsePhase += 0.08 + closeness * 0.12;

    if (++this.trailTick % 4 === 0)
      TrailPool.add(this.x, this.y, SmartEnemy.TRAIL_COLOR, SmartEnemy.SIZE, SmartEnemy.SIZE, 0.06, TrailPool.SHAPE_CIRCLE);
  }

  render(g: Graphics2D): void {
    const g2 = g;
    const ix = Math.trunc(this.x), iy = Math.trunc(this.y);
    const cx = ix + Math.trunc(SmartEnemy.SIZE / 2), cy = iy + Math.trunc(SmartEnemy.SIZE / 2);

    // Breathing pulse — size oscillates, faster when close to player
    const pulse = Math.sin(this.pulsePhase) * 0.5 + 0.5;
    const breathe = Math.trunc(pulse * 4);

    // Outer glow — breathes
    const glowSize = SmartEnemy.SIZE + 8 + breathe * 2;
    g2.setColor(new Color(SmartEnemy.GLOW.getRed(), SmartEnemy.GLOW.getGreen(), SmartEnemy.GLOW.getBlue(),
      25 + Math.trunc(pulse * 20)));
    g2.fillOval(cx - Math.trunc(glowSize / 2), cy - Math.trunc(glowSize / 2), glowSize, glowSize);

    // Main circle — breathes
    const mainSize = SmartEnemy.SIZE + breathe;
    g2.setColor(SmartEnemy.FILL);
    g2.fillOval(cx - Math.trunc(mainSize / 2), cy - Math.trunc(mainSize / 2), mainSize, mainSize);

    // Inner ring — rotating crosshair that tracks the player
    const trackAngle = Math.atan2(this.player.getY() + 24 - cy, this.player.getX() + 24 - cx);
    const innerR = Math.trunc(SmartEnemy.SIZE / 2) - 4;
    g2.setColor(new Color(255, 255, 255, 40 + Math.trunc(pulse * 40)));
    for (let i = 0; i < 4; i++) {
      const a = trackAngle + i * (Math.PI / 2);
      const dx = Math.trunc(Math.cos(a) * innerR);
      const dy = Math.trunc(Math.sin(a) * innerR);
      g2.drawLine(cx + Math.trunc(dx / 2), cy + Math.trunc(dy / 2), cx + dx, cy + dy);
    }
  }
}
